/**
 * SCH — Cadence Service
 * Nodes: N#1069 (BRDs 1355–1364) + N#1070 (BRDs 1365–1372) + N#1071 (BRDs 1373–1382)
 *      + N#1072 (BRDs 1383–1391) + N#1073 (BRDs 1392–1404) + N#1074 (BRDs 1405–1411)
 *      + N#1075 (BRDs 1412–1420) + N#1079 (Engine Tick Loop)
 *      + N#1080 (Scope Materialisation at Fire Time)
 *      + N#1081 (Applicability Evaluation)
 *      + N#1082 (Due-Date Computation)
 *      + N#1083 (Anchor Event Subscription & State)
 *      + N#1084 (Date-Exception Application)
 *      + N#1085 (Event-Key Construction)
 *      + N#1086 (Register Check / Skip-on-Exists)
 *      + N#1087 (Handler Invocation / Fire Contract)
 *      + N#1088 (Idempotency Keys)
 *      + N#1089 (Retry & Dead-Letter Handling)
 *      + N#1090 (Version Resolution at Fire Time)
 *      + N#1091 (Fire Logging)
 *      + N#1092 (Versioning Model — Bead B#147)
 *      + N#1093 (Roll-out & Effective Date)
 *      + N#1094 (Mid-Period Transition Handling)
 *      + N#1095 (Catch-Up Across Version Boundary)
 */
import apiClient from './apiService';
import { AxiosResponse } from 'axios';

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
}

const BASE = '/api/v1/sch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CadenceCategory {
  id: number; name: string; description?: string; is_active: number;
}

// (CadenceType + getTypes removed in migration 066 — category goes directly
// to source obligation, no intermediate type classification.)

// N#1070 BRDs 3 & 4 — Role master list
export interface CadenceRole {
  id: number; name: string; description?: string; is_active: number;
}

export interface SourceObligationItem {
  source_module: string;
  source_type: string;
  source_id: number;
  code: string;
  name: string;
  description?: string;
  template_reference: string;
  // N#1070 BRD 1 — authority fields (read-only mirrors)
  regulatory_source?: string;
  reference_url?: string;
  policy_ref?: string;
}

export type RuleStatus =
  | 'Draft'
  | 'Active'
  | 'Paused'
  | 'Paused - Source archived'
  | 'Paused — Applicability source archived'   // N#1072 BRD 9
  | 'Paused — Trigger source archived'          // N#1073 BRD 13
  | 'Retired';

// N#1072 — Applicability mode
export type ApplicabilityMode = 'unconditional' | 'threshold' | 'questionnaire';

// N#1073 — Trigger mode + sub-types
export type TriggerMode = 'calendar' | 'event';
export type TriggerSpan = 'short' | 'long';
export type CalendarFrequency =
  | 'daily' | 'weekly' | 'fortnightly' | 'monthly'
  | 'bi_monthly' | 'quarterly' | 'half_yearly' | 'annual' | 'biennial';
export type MonthlySpecifier = 'first_day' | 'last_day' | 'nth_weekday' | 'specified_day';
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
export type TriggerVariant = 'after_event' | 'watch_not_happened';
export type OffsetUnit = 'days' | 'weeks' | 'months' | 'years';
export type AnchorFrom = 'completion_date' | 'due_date';
export type FirstFireMode = 'wait_first_anchor' | 'seed_date';
export type DeadlineKind = 'absolute' | 'offset';
export type DeadlineReference = 'period_start' | 'period_end';

export interface CadenceRule {
  id: number;
  entity_id: number;
  category_id: number;
  category_name?: string;
  // Migration 114 — polymorphic source binding (canonical).
  source_type_id?: number;
  source_record_id?: number;
  source_type_code?: string;     // e.g. 'SOP' | 'RISK_ASSESSMENT_TEMPLATE' | 'CONTROL_TEST_TEMPLATE'
  source_display_name?: string;  // registry.display_name
  source_module_code?: string;   // registry.module_code
  // Compat shim (migration 115) — derived from registry; engine still reads these.
  source_module?: string;
  source_type?: string;
  source_obligation_id?: number;
  // N#1069 BRDs 4 & 5 — live-mirrored, read-only
  obligation_name?: string;
  obligation_description?: string;
  // N#1070 BRDs 1 & 2 — authority mirrors, read-only, no edit affordance
  regulatory_source?: string;
  reference_url?: string;
  policy_ref?: string;
  // N#1069 BRD 6 — engine handler reference, read-only
  fulfillment_handler?: string;
  /** Compat shim (migration 115). Synthetic value derived from registry. */
  template_reference?: string;
  // N#1070 BRDs 3 & 4 — roles
  accountable_role_id?: number;
  accountable_role_name?: string;
  fallback_role_id?: number;
  fallback_role_name?: string;
  // N#1069 BRDs 7 & 8
  start_date?: string;
  end_date?: string;
  // N#1072 — Applicability mode + refs
  applicability_mode?: ApplicabilityMode;
  applicability_threshold_id?: number;
  applicability_questionnaire_id?: number;
  predicate_combiner?: 'AND' | 'OR';
  // N#1073 — Trigger mode summary
  trigger_mode?: TriggerMode | null;
  rule_status: RuleStatus;
  is_active: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

export interface CadenceRuleCreate {
  /** Legacy column — omit. The backend derives it from the bound source kind;
   *  category ids are not portable across installs. */
  category_id?: number;
  source_type_id: number;        // FK → UTL_entity_registry
  source_record_id: number;      // record id within registry.table_name
  accountable_role_id?: number;
  fallback_role_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface CadenceRuleUpdate {
  category_id?: number;
  source_type_id?: number;
  source_record_id?: number;
  accountable_role_id?: number;
  fallback_role_id?: number;
  start_date?: string;
  end_date?: string;
}

// Migration 114 — picker item for /sch/source-templates?type_code=...
export interface SourceTemplateItem {
  source_type_id: number;
  source_type_code: string;
  source_record_id: number;
  code?: string | null;
  name: string;
  display_name: string;
  module_code: string;
}

// Migration 114 — manual cadence dispatch payload.
export interface DispatchFireRequest {
  location_id: number;
  maker_id: number;
  checker_id: number;
  start_date?: string;
  end_date?: string;
  due_date?: string;
  cadence_fire_id?: number;
}

export interface DispatchFireResult {
  kind: string;
  table: string;
  instance_id: number | null;
  programs_created?: number[];
}

export interface EventInstance {
  id: number;
  entity_id: number;
  cadence_rule_id: number;
  event_key: string;
  scheduled_date: string;
  // N#1069 snapshots
  obligation_name_snapshot?: string;
  obligation_description_snapshot?: string;
  // N#1070 BRD 2 authority snapshots
  regulatory_source_snapshot?: string;
  reference_url_snapshot?: string;
  policy_ref_snapshot?: string;
  // N#1070 BRDs 6 & 7 assignee
  assignee_role?: string;
  assignee_user_id?: number;
  assignment_note?: string;  // 'direct' | 'fallback used' | 'Unassigned'
  status: string;
  created_on?: string;
}

export interface AdminAlert {
  id: number;
  entity_id: number;
  rule_id?: number;
  event_instance_id?: number;
  alert_type?: string;
  message?: string;
  is_resolved: number;
  created_on?: string;
  resolved_on?: string;
}

// ---------------------------------------------------------------------------
// N#1069 BRD 1 — Categories
// ---------------------------------------------------------------------------
export async function getCategories(): Promise<CadenceCategory[]> {
  const res: AxiosResponse<ApiEnvelope<CadenceCategory[]>> = await apiClient.get(`${BASE}/categories`);
  return res.data.data || [];
}

// ---------------------------------------------------------------------------
// N#1069 BRD 2 — Types
// ---------------------------------------------------------------------------
// (getTypes removed in migration 066 — see CadenceType comment above.)

// ---------------------------------------------------------------------------
// N#1070 BRDs 3 & 4 — Roles (master-seeded, read-only for tenants)
// ---------------------------------------------------------------------------
export async function getRoles(): Promise<CadenceRole[]> {
  const res: AxiosResponse<ApiEnvelope<CadenceRole[]>> = await apiClient.get(`${BASE}/roles`);
  return res.data.data || [];
}

// ---------------------------------------------------------------------------
// Migration 114 — source-template picker (registry-driven). Replaces the
// legacy /source-obligations endpoint that hardcoded COM/ADT.
// ---------------------------------------------------------------------------
export async function getSourceTemplates(typeCode: string): Promise<SourceTemplateItem[]> {
  const res: AxiosResponse<ApiEnvelope<SourceTemplateItem[]>> = await apiClient.get(
    `${BASE}/source-templates`, { params: { type_code: typeCode } }
  );
  return res.data.data || [];
}

// Legacy SourceObligationItem fetcher — kept as a no-op stub for any caller
// not yet migrated. New callers must use getSourceTemplates.
export async function getSourceObligations(_category: string): Promise<SourceObligationItem[]> {
  return [];
}

// ---------------------------------------------------------------------------
// Cadence Rules CRUD
// ---------------------------------------------------------------------------
export async function getRules(params?: {
  rule_status?: string;
  category_id?: number;
  source_type_id?: number;
  source_record_id?: number;
  source_type_codes?: string; // CSV — in-context module filter
}): Promise<CadenceRule[]> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule[]>> = await apiClient.get(`${BASE}/rules`, { params });
  return res.data.data || [];
}

/** One page of records + the unfiltered-by-page total, matching the repo's
 *  `{data, meta:{total}}` paginated envelope. */
export interface PagedResult<T> {
  items: T[];
  total: number;
}

/** Server-paginated rules list — backend applies search + status filter
 *  AFTER enrichment (obligation name / displayed status are derived), so they
 *  are real server-side filters, not page-local ones. Use this for list views;
 *  use `getRules()` when you need every rule (dropdowns, in-context pickers). */
export async function getRulesPaged(opts: {
  page: number;
  limit: number;
  search?: string;
  rule_status?: string;        // exact, or 'Paused' for any Paused-* state
  source_type_codes?: string;  // CSV — in-context module filter
}): Promise<PagedResult<CadenceRule>> {
  const params: Record<string, any> = { page: opts.page, limit: opts.limit };
  if (opts.search) params.search = opts.search;
  if (opts.rule_status) params.rule_status = opts.rule_status;
  if (opts.source_type_codes) params.source_type_codes = opts.source_type_codes;
  const res = await apiClient.get(`${BASE}/rules`, { params });
  const d: any = res.data ?? {};
  return { items: Array.isArray(d.data) ? d.data : [], total: d.meta?.total ?? d.total ?? 0 };
}

export async function getRule(id: number): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.get(`${BASE}/rules/${id}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Rule not found');
}

export async function createRule(payload: CadenceRuleCreate): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.post(`${BASE}/rules`, payload);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to create cadence rule');
}

export async function updateRule(id: number, payload: CadenceRuleUpdate): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.patch(`${BASE}/rules/${id}`, payload);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to update cadence rule');
}

export async function deleteRule(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/rules/${id}`);
}

// Migration 114 — manual cadence dispatch (creates one rollout program for
// the rule's bound template at the supplied location).
export async function dispatchFire(
  ruleId: number,
  payload: DispatchFireRequest
): Promise<DispatchFireResult> {
  const res: AxiosResponse<ApiEnvelope<DispatchFireResult>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/dispatch-fire`, payload
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Dispatch failed');
}

// N#1069 BRD 7 — Activate
export async function activateRule(id: number, start_date: string): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.post(`${BASE}/rules/${id}/activate`, { start_date });
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to activate rule');
}

export async function pauseRule(id: number): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.post(`${BASE}/rules/${id}/pause`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to pause rule');
}

export async function resumeRule(id: number): Promise<CadenceRule> {
  const res: AxiosResponse<ApiEnvelope<CadenceRule>> = await apiClient.post(`${BASE}/rules/${id}/resume`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to resume rule');
}

// N#1069 BRD 9 + N#1070 BRDs 6 & 7 — Fire
export async function fireRule(
  id: number,
  scheduled_date: string,
  location_id?: number
): Promise<{ skipped: boolean; data?: EventInstance; event_key?: string; reason?: string }> {
  const res = await apiClient.post(`${BASE}/rules/${id}/fire`, null, {
    params: { scheduled_date, ...(location_id ? { location_id } : {}) }
  });
  return res.data;
}

// Manual rollout vent — fire this rule's due occurrences NOW (enqueue + formup
// via the same resolve→dispatch path). Placed on the cadence engine for testing;
// will also surface on each module's own screen later.
export async function rolloutNow(
  id: number,
  opts?: { start?: string; window_days?: number }
): Promise<{ enqueue: { tentative_inserted: number }; formup: { calendar_committed: number; run_id: number } }> {
  const res = await apiClient.post(`${BASE}/rules/${id}/rollout-now`, null, {
    params: { ...(opts?.start ? { start: opts.start } : {}), ...(opts?.window_days ? { window_days: opts.window_days } : {}) }
  });
  return res.data?.data ?? res.data;
}

export async function getRuleEvents(id: number): Promise<EventInstance[]> {
  const res: AxiosResponse<ApiEnvelope<EventInstance[]>> = await apiClient.get(`${BASE}/rules/${id}/events`);
  return res.data.data || [];
}

// N#1069 BRD 10 — Check source status
export async function checkSourceStatus(): Promise<{ paused_rule_ids: number[]; message: string }> {
  const res = await apiClient.post(`${BASE}/check-source-status`);
  return res.data;
}

// N#1070 BRD 7 — Admin alerts
export async function getAdminAlerts(): Promise<AdminAlert[]> {
  const res: AxiosResponse<ApiEnvelope<AdminAlert[]>> = await apiClient.get(`${BASE}/admin-alerts`);
  return res.data.data || [];
}

export async function assignEvent(eventId: number, assigneeUserId: number): Promise<EventInstance> {
  const res: AxiosResponse<ApiEnvelope<EventInstance>> = await apiClient.post(
    `${BASE}/events/${eventId}/assign`, null, { params: { assignee_user_id: assigneeUserId } }
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Assignment failed');
}

// ===========================================================================
// N#1071 — Scope (Who)  BRDs 1373–1382
// ===========================================================================

export type ScopeMode = 'specific' | 'region_based';
export type ScopeLocationStatus = 'active' | 'missing';

export interface OrgLocation {
  id: number;
  name: string;
  parent_location_id?: number | null;
  location_type?: string | null;
  is_active: number;
}

export interface ScopeLocation {
  id?: number;
  location_id: number;
  location_name?: string;
  location_type?: string | null;
  status: ScopeLocationStatus;
}

export interface ScopeConfig {
  id?: number;
  entity_id?: number;
  rule_id: number;
  scope_mode: ScopeMode;
  // Legacy single-FK fields — left for backward compat reads of very old rows.
  specific_location_id?: number | null;
  specific_location_name?: string | null;
  // New multi-pick (BRD 2 post-migration 065): N cherry-picked geographies.
  specific_location_ids?: number[];
  specific_locations?: ScopeLocation[];
  region_anchor_id?: number | null;
  region_anchor_name?: string | null;   // 'Global' when anchor is null
  // N#1071 — type filter references the ORG_geography_types master.
  geography_type_id?: number | null;
  geography_type_name?: string | null;
  type_filter?: string | null;          // legacy free-text (read-only)
  filter_annotation?: string | null;
  materialised_at?: string | null;
  needs_refresh: number;
  locations?: ScopeLocation[];          // materialised list
}

export interface ScopeSavePayload {
  scope_mode: ScopeMode;
  // Multi-pick for specific mode; the singular field is no longer accepted on save.
  specific_location_ids?: number[];
  region_anchor_id?: number | null;
  geography_type_id?: number | null;
  filter_annotation?: string | null;
}

// N#1071 — geography-type master row for the scope type filter.
export interface GeographyType {
  id: number;
  name: string;
}

export interface ScopeDiffEntry {
  location_id: number;
  location_name?: string;
  location_type?: string | null;
  reason: string;
}

export interface ScopeDiff {
  rule_id: number;
  needs_refresh: number;
  added: ScopeDiffEntry[];
  removed: ScopeDiffEntry[];
}

export interface ScopeRefreshLogEntry {
  id: number;
  rule_id: number;
  actor_user_id?: number;
  actor_name?: string;
  change_reason?: string;
  added_location_ids: number[];
  removed_location_ids: number[];
  created_on?: string;
}

// BRD 1, 3 — Geography register
export async function getScopeLocations(typeFilter?: string): Promise<OrgLocation[]> {
  const res: AxiosResponse<ApiEnvelope<OrgLocation[]>> = await apiClient.get(
    `${BASE}/locations`,
    { params: typeFilter ? { type_filter: typeFilter } : {} },
  );
  return res.data.data || [];
}

export async function getLocationTypes(): Promise<string[]> {
  const res: AxiosResponse<ApiEnvelope<string[]>> = await apiClient.get(`${BASE}/location-types`);
  return res.data.data || [];
}

// N#1071 — geography-type master (ORG_geography_types) for the scope type filter.
export async function getGeographyTypes(): Promise<GeographyType[]> {
  const res: AxiosResponse<ApiEnvelope<GeographyType[]>> = await apiClient.get(`${BASE}/geography-types`);
  return res.data.data || [];
}

// BRDs 1–5 — Save / read scope
export async function getRuleScope(ruleId: number): Promise<ScopeConfig | null> {
  const res: AxiosResponse<ApiEnvelope<ScopeConfig | null> & { locations?: ScopeLocation[] }> = await apiClient.get(
    `${BASE}/rules/${ruleId}/scope`,
  );
  return res.data.data || null;
}

// N#1071 — live preview: resolve a scope config to qualifying geographies
// without saving (same resolver as save). Used by the Geographies step.
export async function previewScope(payload: ScopeSavePayload): Promise<ScopeLocation[]> {
  const res: AxiosResponse<ApiEnvelope<ScopeLocation[]>> = await apiClient.post(`${BASE}/scope/preview`, payload);
  return res.data.data || [];
}

export async function saveRuleScope(ruleId: number, payload: ScopeSavePayload): Promise<ScopeConfig> {
  const res: AxiosResponse<ApiEnvelope<ScopeConfig>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/scope`,
    payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to save scope');
}

// BRD 6 — Diff between current filter and stored materialised list
export async function getScopeDiff(ruleId: number): Promise<ScopeDiff> {
  const res: AxiosResponse<ApiEnvelope<ScopeDiff>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/scope/diff`,
  );
  return res.data.data || { rule_id: ruleId, needs_refresh: 0, added: [], removed: [] };
}

// BRD 6 — Tenant-wide recompute (scan all rules)
export async function recomputeScopeFlags(): Promise<{ flagged_rule_ids: number[]; total: number }> {
  const res = await apiClient.post(`${BASE}/scope/recompute-flags`);
  return res.data;
}

// BRDs 7, 8 — Owner approve/decline refresh
export async function approveScopeRefresh(
  ruleId: number,
  decision: 'approve' | 'decline',
  changeReason?: string,
): Promise<{ decision: string; added?: number[]; removed?: number[] }> {
  const res = await apiClient.post(`${BASE}/rules/${ruleId}/scope/refresh`, {
    decision,
    change_reason: changeReason,
  });
  return res.data;
}

// BRD 8 — Audit log
export async function getScopeRefreshLog(ruleId: number): Promise<ScopeRefreshLogEntry[]> {
  const res: AxiosResponse<ApiEnvelope<ScopeRefreshLogEntry[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/scope/refresh-log`,
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1072 — Applicability (When applicable)  BRDs 1383–1391
// ===========================================================================

export type PredicateOperator = 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains';

export interface ThresholdLibraryItem {
  id: number;
  entity_id: number;
  name: string;
  description?: string | null;
  expression: string;
  evaluation_context?: string | null;
  fields_used: any[];
  // N#1072 master attributes (migration 167)
  periodicity_id?: number | null;
  accumulation_type: string;          // 'accumulating' | 'one_off'
  is_geo_tagged: boolean;
  is_active: number;
  reference_count?: number;   // populated on /threshold-library/{tid}
  // Maker-checker workflow (migration 20260625_072934)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  seq_code?: string | null;
}

export interface Periodicity { id: number; name: string }

export interface QuestionnaireQuestion {
  id: number;
  questionnaire_id: number;
  question_code: string;
  question_text: string;
  question_type: 'text' | 'number' | 'boolean' | 'choice';
  options: any[];
  display_order: number;
  is_active: number;
}

export interface QuestionnaireLibraryItem {
  id: number;
  entity_id: number;
  code: string;
  name: string;
  description?: string | null;
  activation_cadence?: string | null;
  is_active: number;
  questions?: QuestionnaireQuestion[];
  reference_count?: number;
}

export interface PredicateConditionInput {
  question_id: number;
  operator: PredicateOperator;
  expected_value: string;
  display_order: number;
}

export interface PredicateConditionResponse extends PredicateConditionInput {
  id: number;
  rule_id: number;
  question_code?: string;
  question_text?: string;
}

export interface ApplicabilityPayload {
  mode: ApplicabilityMode;
  threshold_id?: number | null;
  questionnaire_id?: number | null;
  predicate_combiner?: 'AND' | 'OR';
  conditions?: PredicateConditionInput[];
}

export interface ApplicabilityConfig {
  rule_id: number;
  mode: ApplicabilityMode;
  threshold_id?: number | null;
  threshold?: ThresholdLibraryItem | null;
  questionnaire_id?: number | null;
  questionnaire?: QuestionnaireLibraryItem | null;
  predicate_combiner: 'AND' | 'OR';
  conditions: PredicateConditionResponse[];
  warnings: string[];
}

// BRD 3 — Threshold Library (read for picker; live-mirror)
export async function getThresholdLibrary(): Promise<ThresholdLibraryItem[]> {
  const res: AxiosResponse<ApiEnvelope<ThresholdLibraryItem[]>> = await apiClient.get(`${BASE}/threshold-library`);
  return res.data.data || [];
}

export async function getThreshold(id: number): Promise<ThresholdLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<ThresholdLibraryItem>> = await apiClient.get(`${BASE}/threshold-library/${id}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Threshold not found');
}

// N#1072 — global periodicity lookup (read-only)
export async function getPeriodicities(): Promise<Periodicity[]> {
  const res: AxiosResponse<ApiEnvelope<Periodicity[]>> = await apiClient.get(`${BASE}/periodicities`);
  return res.data.data || [];
}

// ── Threshold library CRUD (Org-Masters) ──────────────────────────
export async function createThreshold(body: {
  name: string; description?: string;
  periodicity_id?: number | null; accumulation_type: string; is_geo_tagged: boolean;
}, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<ThresholdLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<ThresholdLibraryItem>> = await apiClient.post(
    `${BASE}/threshold-library`, body, { params: { action } });
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to create threshold');
}

export async function updateThreshold(id: number, body: Partial<{
  name: string; description: string;
  periodicity_id: number | null; accumulation_type: string; is_geo_tagged: boolean; is_active: number;
}>): Promise<ThresholdLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<ThresholdLibraryItem>> = await apiClient.patch(`${BASE}/threshold-library/${id}`, body);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to update threshold');
}

export async function deleteThreshold(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/threshold-library/${id}`);
}

// BRD 4 — Questionnaire Library (read for picker; live-mirror)
export async function getQuestionnaireLibrary(): Promise<QuestionnaireLibraryItem[]> {
  const res: AxiosResponse<ApiEnvelope<QuestionnaireLibraryItem[]>> = await apiClient.get(`${BASE}/questionnaire-library`);
  return res.data.data || [];
}

export async function getQuestionnaire(id: number): Promise<QuestionnaireLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<QuestionnaireLibraryItem>> = await apiClient.get(`${BASE}/questionnaire-library/${id}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Questionnaire not found');
}

// N#1072 — Checklist source for the (checklist-driven) applicability picker.
// Sources audit GRC-Common Checklist procedures + their yes/no items.
export interface Checklist { id: number; name: string }
export interface ChecklistItem { id: number; srno: number; check_question: string }

export async function getChecklists(): Promise<Checklist[]> {
  const res: AxiosResponse<ApiEnvelope<Checklist[]>> = await apiClient.get(`${BASE}/checklists`);
  return res.data.data || [];
}

export async function getChecklistItems(procedureId: number): Promise<ChecklistItem[]> {
  const res: AxiosResponse<ApiEnvelope<ChecklistItem[]>> = await apiClient.get(`${BASE}/checklists/${procedureId}/items`);
  return res.data.data || [];
}

// BRDs 1–5 — Applicability on a rule
export async function getRuleApplicability(ruleId: number): Promise<ApplicabilityConfig> {
  const res: AxiosResponse<ApiEnvelope<ApplicabilityConfig>> = await apiClient.get(`${BASE}/rules/${ruleId}/applicability`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load applicability');
}

export async function saveRuleApplicability(ruleId: number, payload: ApplicabilityPayload): Promise<ApplicabilityConfig> {
  const res: AxiosResponse<ApiEnvelope<ApplicabilityConfig>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/applicability`,
    payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to save applicability');
}

// BRD 7 — submission / evaluation publish endpoints
export async function submitQuestionnaire(payload: {
  questionnaire_id: number;
  rule_id?: number;
  occurrence_date: string;
  answers: Record<string, string>;
}) {
  const res = await apiClient.post(`${BASE}/questionnaire-submissions`, payload);
  return res.data;
}

export async function publishThresholdEvaluation(payload: {
  threshold_id: number;
  location_id: number;
  result: boolean;
}) {
  const res = await apiClient.post(`${BASE}/threshold-evaluations`, payload);
  return res.data;
}

// BRD 9 — scan & auto-pause
export async function checkApplicabilitySources(): Promise<{ paused_rule_ids: number[]; message: string }> {
  const res = await apiClient.post(`${BASE}/check-applicability-sources`);
  return res.data;
}

// ===========================================================================
// N#1073 — Due-Date Trigger (When due)  BRDs 1392–1404
// ===========================================================================

export interface EventLibraryItem {
  id: number;
  entity_id: number;
  code: string;
  name: string;
  event_type?: string | null;
  source_module?: string | null;
  event_schema: any;
  description?: string | null;
  // N#1073 master attributes (migration 167)
  measure_periodically: boolean;
  is_geo_tagged: boolean;
  is_active: number;
  reference_count?: number;
  // Maker-checker workflow (migration 20260625_071416)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  seq_code?: string | null;
}

export interface TriggerConfig {
  rule_id: number;
  mode: TriggerMode;
  // Calendar
  span?: TriggerSpan | null;
  frequency?: CalendarFrequency | null;
  day_of_week?: DayOfWeek | null;
  monthly_specifier?: MonthlySpecifier | null;
  nth_weekday_n?: number | null;
  nth_weekday_day?: DayOfWeek | null;
  specified_day?: number | null;
  month_of_period?: number | null;
  // Event
  event_id?: number | null;
  event?: EventLibraryItem | null;
  variant?: TriggerVariant | null;
  offset_value?: number | null;
  offset_unit?: OffsetUnit | null;
  anchor_from?: AnchorFrom | null;
  watch_grace_days?: number | null;
  first_fire_mode?: FirstFireMode | null;
  seed_date?: string | null;
  deadline_kind?: DeadlineKind | null;
  deadline_date?: string | null;
  deadline_offset_days?: number | null;
  deadline_reference?: DeadlineReference | null;
  period_token_format?: string;
  warnings?: string[];
}

export interface TriggerSavePayload {
  mode: TriggerMode;
  span?: TriggerSpan | null;
  frequency?: CalendarFrequency | null;
  day_of_week?: DayOfWeek | null;
  monthly_specifier?: MonthlySpecifier | null;
  nth_weekday_n?: number | null;
  nth_weekday_day?: DayOfWeek | null;
  specified_day?: number | null;
  month_of_period?: number | null;
  event_id?: number | null;
  variant?: TriggerVariant | null;
  offset_value?: number | null;
  offset_unit?: OffsetUnit | null;
  anchor_from?: AnchorFrom | null;
  watch_grace_days?: number | null;
  first_fire_mode?: FirstFireMode | null;
  seed_date?: string | null;
  deadline_kind?: DeadlineKind | null;
  deadline_date?: string | null;
  deadline_offset_days?: number | null;
  deadline_reference?: DeadlineReference | null;
}

// BRD 6 — Event Library
export async function getEventLibrary(): Promise<EventLibraryItem[]> {
  const res: AxiosResponse<ApiEnvelope<EventLibraryItem[]>> = await apiClient.get(`${BASE}/event-library`);
  return res.data.data || [];
}

export async function getEvent(id: number): Promise<EventLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<EventLibraryItem>> = await apiClient.get(`${BASE}/event-library/${id}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Event not found');
}

// ── Event library CRUD (Org-Masters) ──────────────────────────────
export async function createEvent(body: {
  name: string; description?: string;
  measure_periodically: boolean; is_geo_tagged: boolean;
}, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<EventLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<EventLibraryItem>> = await apiClient.post(
    `${BASE}/event-library`, body, { params: { action } });
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to create event');
}

export async function updateEvent(id: number, body: Partial<{
  name: string; description: string;
  measure_periodically: boolean; is_geo_tagged: boolean; is_active: number;
}>): Promise<EventLibraryItem> {
  const res: AxiosResponse<ApiEnvelope<EventLibraryItem>> = await apiClient.patch(`${BASE}/event-library/${id}`, body);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to update event');
}

export async function deleteEvent(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/event-library/${id}`);
}

export async function publishEventOccurrence(eventId: number, payload: {
  location_id?: number;
  occurred_on: string;
  completion_date?: string;
  due_date?: string;
  payload?: any;
}) {
  const res = await apiClient.post(`${BASE}/event-library/${eventId}/occurrences`, { event_id: eventId, ...payload });
  return res.data;
}

// BRDs 1–11 — Trigger config on a rule
export async function getRuleTrigger(ruleId: number): Promise<TriggerConfig | null> {
  const res: AxiosResponse<ApiEnvelope<TriggerConfig | null>> = await apiClient.get(`${BASE}/rules/${ruleId}/trigger`);
  return res.data.data || null;
}

export async function saveRuleTrigger(ruleId: number, payload: TriggerSavePayload): Promise<TriggerConfig> {
  const res: AxiosResponse<ApiEnvelope<TriggerConfig>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/trigger`,
    payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to save trigger');
}

// BRDs 9–11 — Tick (engine entry; evaluates trigger to decide fire/skip/wait)
export async function tickRule(ruleId: number, as_of: string, location_id?: number): Promise<any> {
  const res = await apiClient.post(`${BASE}/rules/${ruleId}/tick`, { as_of, location_id });
  return res.data;
}

// BRD 13 — Scan & auto-pause
export async function checkTriggerSources(): Promise<{ paused_rule_ids: number[]; message: string }> {
  const res = await apiClient.post(`${BASE}/check-trigger-sources`);
  return res.data;
}

// ===========================================================================
// N#1074 — Date Exceptions (What if)  BRDs 1405–1411
// ===========================================================================

export type DateExceptionAction =
  | 'strict' | 'shift_forward' | 'shift_backward' | 'shift_to_nearest';

export interface HolidayDate {
  id: number;
  calendar_id: number;
  holiday_date: string;
  name?: string | null;
  is_active: number;
}

export interface HolidayCalendar {
  id: number;
  entity_id: number;
  code: string;
  name: string;
  description?: string | null;
  is_active: number;
  holidays?: HolidayDate[];
}

export interface DateExceptionConfig {
  rule_id: number;
  action: DateExceptionAction;
  business_days: number[];   // ISO weekday 1..7 (Mon..Sun)
  holiday_calendar_id?: number | null;
  holiday_calendar?: HolidayCalendar | null;
}

export interface DateExceptionPayload {
  action: DateExceptionAction;
  business_days: number[];
  holiday_calendar_id?: number | null;
}

export interface DryRunRow {
  period_token: string;
  period_start?: string;
  period_end?: string;
  // NULL for event-mode rows (frontend renders symbolic `x ± N <unit>`)
  original_date?: string | null;
  final_date?: string | null;
  shifted: boolean;
  reason?: string | null;
  mode?: 'calendar' | 'event';
}

export interface DryRunResult {
  rule_id: number;
  rows: DryRunRow[];
  mode?: 'calendar' | 'event' | null;
}

// BRD 3 — Holiday calendars
export async function getHolidayCalendars(): Promise<HolidayCalendar[]> {
  const res: AxiosResponse<ApiEnvelope<HolidayCalendar[]>> = await apiClient.get(`${BASE}/holiday-calendars`);
  return res.data.data || [];
}

export async function getHolidayCalendar(id: number): Promise<HolidayCalendar> {
  const res: AxiosResponse<ApiEnvelope<HolidayCalendar>> = await apiClient.get(`${BASE}/holiday-calendars/${id}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Calendar not found');
}

// BRDs 1–4 — Per-rule date-exception config
export async function getRuleDateExceptions(ruleId: number): Promise<DateExceptionConfig> {
  const res: AxiosResponse<ApiEnvelope<DateExceptionConfig>> = await apiClient.get(`${BASE}/rules/${ruleId}/date-exceptions`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load date exceptions');
}

export async function saveRuleDateExceptions(ruleId: number, payload: DateExceptionPayload): Promise<DateExceptionConfig> {
  const res: AxiosResponse<ApiEnvelope<DateExceptionConfig>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/date-exceptions`, payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to save date exceptions');
}

// BRD 7 — Dry-run preview. By default walks the rule's full lifespan
// (capped at 5y for open-ended rules); pass `months` to override that cap.
export async function getRuleDryRun(ruleId: number, months?: number): Promise<DryRunResult> {
  const res: AxiosResponse<ApiEnvelope<DryRunResult>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/dry-run`,
    months != null ? { params: { months } } : undefined,
  );
  return res.data.data || { rule_id: ruleId, rows: [], mode: null };
}

// ===========================================================================
// N#1075 — Parameters (Reference + Tolerances)  BRDs 1412–1420
// ===========================================================================

export type ParametersMode = 'use_defaults' | 'modify';

export interface GlobalParameters {
  id?: number;
  entity_id?: number;
  holiday_calendar_id?: number | null;
  business_day_definition: number[];
  time_zone: string;
  reminder_lead_days: number;
  grace_period_days: number;
  catch_up_window_days: number;
}

export interface RuleParametersConfig {
  rule_id: number;
  mode: ParametersMode;
  // Effective values (server applies BRD 7 cap)
  holiday_calendar_id?: number | null;
  business_day_definition: number[];
  time_zone: string;
  reminder_lead_days: number;
  grace_period_days: number;
  catch_up_window_days: number;
  // Raw local values when mode='modify'
  local_holiday_calendar_id?: number | null;
  local_business_day_definition?: number[] | null;
  local_time_zone?: string | null;
  local_reminder_lead_days?: number | null;
  local_grace_period_days?: number | null;
  local_catch_up_window_days?: number | null;
  // Globals always returned
  global_holiday_calendar_id?: number | null;
  global_business_day_definition: number[];
  global_time_zone: string;
  global_reminder_lead_days: number;
  global_grace_period_days: number;
  global_catch_up_window_days: number;
  catch_up_capped: boolean;
  catch_up_cap_hint?: string | null;
}

export interface RuleParametersSavePayload {
  mode: ParametersMode;
  holiday_calendar_id?: number | null;
  business_day_definition?: number[] | null;
  time_zone?: string | null;
  reminder_lead_days?: number | null;
  grace_period_days?: number | null;
  catch_up_window_days?: number | null;
  confirm_discard?: boolean;
}

export interface DiscardPreview {
  rule_id: number;
  would_lose: boolean;
  irreversible?: boolean;
  warning: string;
  local_values?: {
    holiday_calendar_id?: number | null;
    business_day_definition?: number[] | null;
    time_zone?: string | null;
    reminder_lead_days?: number | null;
    grace_period_days?: number | null;
    catch_up_window_days?: number | null;
  } | null;
}

export interface ParametersAuditEntry {
  id: number;
  entity_id: number;
  changed_field: string;
  before_value?: string | null;
  after_value?: string | null;
  affected_use_defaults_rule_count: number;
  changed_by?: number;
  changed_on?: string;
}

// BRD 2 — Global parameters
export async function getGlobalParameters(): Promise<GlobalParameters> {
  const res: AxiosResponse<ApiEnvelope<GlobalParameters>> = await apiClient.get(`${BASE}/global-parameters`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load global parameters');
}

export async function updateGlobalParameters(payload: Partial<GlobalParameters>): Promise<{ data: GlobalParameters; affected_use_defaults_rule_count: number }> {
  const res = await apiClient.patch(`${BASE}/global-parameters`, payload);
  return res.data;
}

export async function getGlobalParametersAudit(): Promise<ParametersAuditEntry[]> {
  const res: AxiosResponse<ApiEnvelope<ParametersAuditEntry[]>> = await apiClient.get(`${BASE}/global-parameters/audit`);
  return res.data.data || [];
}

// BRDs 1–7 — Per-rule parameters
export async function getRuleParameters(ruleId: number): Promise<RuleParametersConfig> {
  const res: AxiosResponse<ApiEnvelope<RuleParametersConfig>> = await apiClient.get(`${BASE}/rules/${ruleId}/parameters`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load rule parameters');
}

export async function saveRuleParameters(ruleId: number, payload: RuleParametersSavePayload): Promise<RuleParametersConfig> {
  const res: AxiosResponse<ApiEnvelope<RuleParametersConfig>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/parameters`, payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to save rule parameters');
}

// BRD 5 — destructive-switch preview
export async function getRuleParametersDiscardPreview(ruleId: number): Promise<DiscardPreview> {
  const res: AxiosResponse<ApiEnvelope<DiscardPreview>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/parameters/discard-preview`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load discard preview');
}

// ===========================================================================
// N#1079 — Engine Tick Loop  BRDs 1421–1431
// ===========================================================================

export type TickInterval = 'hourly' | 'daily' | 'weekly';
export type CatchUpPolicy = 'backdate_within_window' | 'skip_with_alert';
export type TriggerSource = 'scheduled' | 'manual' | 'dry-run' | 'catch-up';

export type PredicateEvalPolicy = 'every_tick' | 'on_attribute_change' | 'both';

export interface EngineSettings {
  id?: number;
  entity_id?: number;
  tick_interval: TickInterval;
  tick_time_of_day: string;
  tick_time_zone: string;
  is_enabled: number;
  concurrency_cap: number;
  per_rule_throttle_default: number;
  max_lock_duration_seconds: number;
  catch_up_policy: CatchUpPolicy;
  catch_up_window_days: number;
  // N#1081 BRD 6 — global Predicate Evaluation Policy
  predicate_evaluation_policy: PredicateEvalPolicy;
  // N#1083 subscription parameters
  subscription_catch_up_window_days?: number;
  subscription_poll_interval_seconds?: number;
  subscription_staleness_default_days?: number;
  // N#1089 retry / DLQ policy
  retry_max_attempts?: number;
  retry_backoff_minutes?: string;
  dlq_alert_mode?: 'immediate' | 'aggregate' | 'none';
  // N#1091 fire-log retention
  fire_log_retention_years?: number;
}

export interface CadenceRun {
  id: number;
  entity_id: number;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  rules_evaluated: number;
  rules_succeeded: number;
  rules_failed: number;
  fires_emitted: number;
  instances_created: number;
  instances_skipped: number;
  errors: any[];
  trigger_source: TriggerSource;
  triggered_by_user_id?: number;
  commit_writes: boolean;
  status: 'succeeded' | 'partial' | 'lock-expired' | 'skipped' | 'failed';
  skip_reason?: string | null;
  projection?: {
    would_create: any[];
    would_skip: any[];
    would_shift: any[];
    would_invoke?: { rule_id: number; location_id?: number | null; payload: any }[];
  } | null;
}

export interface EngineLockInfo {
  entity_id: number;
  is_locked: number;
  locked_at?: string | null;
  locked_by?: string | null;
  expires_at?: string | null;
  last_release?: string | null;
}

export interface MissedTickEntry {
  id: number;
  entity_id: number;
  missed_at: string;
  detected_at?: string;
  policy_applied?: 'backdated' | 'skipped_with_alert';
  backfilled_dates?: any[];
  notes?: string;
  detected_in_run?: number;
}

export async function getEngineSettings(): Promise<EngineSettings> {
  const res: AxiosResponse<ApiEnvelope<EngineSettings>> = await apiClient.get(`${BASE}/engine/settings`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load engine settings');
}

export async function updateEngineSettings(payload: Partial<EngineSettings>): Promise<EngineSettings> {
  const res: AxiosResponse<ApiEnvelope<EngineSettings>> = await apiClient.patch(`${BASE}/engine/settings`, payload);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to update engine settings');
}

export async function runEngineNow(commit: boolean, ruleId?: number): Promise<CadenceRun> {
  const res: AxiosResponse<ApiEnvelope<CadenceRun>> = await apiClient.post(
    `${BASE}/engine/run`, { commit, rule_id: ruleId },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Engine run failed');
}

export async function listEngineRuns(triggerSource?: TriggerSource, limit: number = 50): Promise<CadenceRun[]> {
  const res: AxiosResponse<ApiEnvelope<CadenceRun[]>> = await apiClient.get(`${BASE}/engine/runs`, {
    params: { ...(triggerSource ? { trigger_source: triggerSource } : {}), limit },
  });
  return res.data.data || [];
}

export async function getEngineRun(runId: number): Promise<CadenceRun> {
  const res: AxiosResponse<ApiEnvelope<CadenceRun>> = await apiClient.get(`${BASE}/engine/runs/${runId}`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Run not found');
}

export async function getEngineLock(): Promise<EngineLockInfo | null> {
  const res: AxiosResponse<ApiEnvelope<EngineLockInfo | null>> = await apiClient.get(`${BASE}/engine/lock`);
  return res.data.data || null;
}

export async function forceReleaseEngineLock(): Promise<{ released: boolean }> {
  const res = await apiClient.post(`${BASE}/engine/lock/release`);
  return res.data;
}

export async function listMissedTicks(limit: number = 100): Promise<MissedTickEntry[]> {
  const res: AxiosResponse<ApiEnvelope<MissedTickEntry[]>> = await apiClient.get(`${BASE}/engine/missed-ticks`, {
    params: { limit },
  });
  return res.data.data || [];
}

// ===========================================================================
// N#1080 — Scope Materialisation at Fire Time  BRDs 1432–1439
// ===========================================================================

export interface RuleVersion {
  id: number;
  entity_id: number;
  rule_id: number;
  version_number: number;
  active_from?: string | null;
  active_until?: string | null;
  rule_snapshot: any;
  scope_snapshot: any[];
  full_snapshot?: any | null;                  // N#1092 BRD 6
  state: 'draft' | 'queued' | 'active' | 'historical';  // N#1092 BRDs 4, 5
  materialised_list_refreshed_on?: string | null;  // N#1092 BRD 7
  materialised_list_refresh_count?: number;
  // N#1093 — rollout / effective-date metadata
  rollout_mode?: 'immediate' | 'future_effective' | null;
  effective_date_changed_at?: string | null;
  effective_date_change_count?: number;
  notes?: string | null;
  created_by?: number;
  created_on?: string;
}

export interface RuleVersionHistory {
  current_version_number: number;
  current_active_from?: string;
  history: RuleVersion[];
}

export interface EngineScopeSkip {
  id: number;
  run_id?: number;
  entity_id: number;
  rule_id: number;
  reason: 'empty_materialised_list' | 'missing_geography';
  location_id?: number;
  occurrence_date?: string;
  created_on?: string;
}

export async function listRuleVersions(ruleId: number): Promise<RuleVersionHistory> {
  const res: AxiosResponse<ApiEnvelope<RuleVersionHistory>> = await apiClient.get(`${BASE}/rules/${ruleId}/versions`);
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load versions');
}

export async function rolloutRuleVersion(ruleId: number, notes?: string): Promise<RuleVersionHistory> {
  const res: AxiosResponse<ApiEnvelope<RuleVersionHistory>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/versions/rollout`, { notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Rollout failed');
}

export async function listRunScopeSkips(runId: number): Promise<EngineScopeSkip[]> {
  const res: AxiosResponse<ApiEnvelope<EngineScopeSkip[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/scope-skips`,
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1081 — Applicability Evaluation  BRDs 1440–1449
// ===========================================================================

export interface MatchedCondition {
  question_code: string;
  operator: string;
  expected: string;
  actual?: string | null;
  match: boolean;
}

export interface ApplicabilityEvaluation {
  id: number;
  entity_id: number;
  run_id?: number;
  rule_id: number;
  mode: 'unconditional' | 'threshold' | 'questionnaire';
  occurrence_date?: string;
  passed?: boolean | null;
  reason?: string | null;
  scope_element_id?: number | null;
  threshold_id?: number | null;
  threshold_used?: string | null;
  value_checked?: string | null;
  value_against?: string | null;
  questionnaire_id?: number | null;
  predicate_text?: string | null;
  submission_id?: number | null;
  matched_conditions?: MatchedCondition[];
  is_catch_up: boolean;
  notes?: string | null;
  created_on?: string;
}

export async function listRunApplicabilityEvaluations(runId: number, ruleId?: number): Promise<ApplicabilityEvaluation[]> {
  const res: AxiosResponse<ApiEnvelope<ApplicabilityEvaluation[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/applicability-evaluations`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleApplicabilityEvaluations(ruleId: number, limit: number = 50): Promise<ApplicabilityEvaluation[]> {
  const res: AxiosResponse<ApiEnvelope<ApplicabilityEvaluation[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/applicability-evaluations`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1082 — Due-Date Computation  BRDs 1450–1459
// ===========================================================================

export interface DueDateComputation {
  id: number;
  entity_id: number;
  run_id?: number;
  rule_id: number;
  scope_element_id?: number | null;
  occurrence_as_of?: string;
  computed_due_date?: string | null;
  mode?: 'calendar' | 'event' | null;
  span?: 'short' | 'long' | null;
  frequency?: string | null;
  specifier?: string | null;
  variant?: 'after_event' | 'watch_not_happened' | null;
  anchor_event_id?: number | null;
  anchor_occurrence_id?: number | null;
  anchor_timestamp?: string | null;
  period_token?: string | null;
  skipped: boolean;
  skip_reason?: string | null;
  path_notes?: string | null;
  is_catch_up: boolean;
  created_on?: string;
}

export async function listRunDueDateComputations(runId: number, ruleId?: number): Promise<DueDateComputation[]> {
  const res: AxiosResponse<ApiEnvelope<DueDateComputation[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/due-date-computations`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleDueDateComputations(ruleId: number, limit: number = 50): Promise<DueDateComputation[]> {
  const res: AxiosResponse<ApiEnvelope<DueDateComputation[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/due-date-computations`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1083 — Anchor Event Subscription & State  BRDs 1460–1468
// ===========================================================================

export type SubscriptionKind = 'event' | 'questionnaire' | 'obligation';

export interface EngineSubscription {
  id: number;
  entity_id: number;
  rule_id: number;
  source_kind: SubscriptionKind;
  source_ref_id: number;
  is_paused: number;
  pause_reason?: string | null;
  last_event_at?: string | null;
  last_heartbeat_at?: string | null;
  error_count: number;
  last_error?: string | null;
  staleness_threshold_days?: number | null;
  is_stale?: boolean;
  stale_for_days?: number | null;
  source_label?: string | null;
  rule_status?: string;
  created_on?: string;
  modified_on?: string;
}

export interface AnchorState {
  id: number;
  entity_id: number;
  rule_id: number;
  event_id: number;
  occurrence_id?: number | null;
  location_id?: number | null;
  occurred_on?: string | null;
  completion_date?: string | null;
  due_date?: string | null;
  payload?: any;
  received_at?: string | null;
  history_count: number;
  event_code?: string | null;
  event_name?: string | null;
}

export interface AnchorStateLog {
  id: number;
  entity_id: number;
  rule_id: number;
  event_id?: number | null;
  occurrence_id?: number | null;
  action: 'received' | 'updated' | 'cleared' | 'error';
  before_state?: any;
  after_state?: any;
  received_at?: string | null;
  notes?: string | null;
}

export interface SubscriptionCatchUpResult {
  window_days: number;
  occurrences_replayed: number;
  affected_rule_ids: number[];
  unrecovered_old?: number;
}

export async function listEngineSubscriptions(): Promise<EngineSubscription[]> {
  const res: AxiosResponse<ApiEnvelope<EngineSubscription[]>> = await apiClient.get(`${BASE}/engine/subscriptions`);
  return res.data.data || [];
}

export async function refreshSubscriptions(windowDays?: number): Promise<SubscriptionCatchUpResult> {
  const res: AxiosResponse<ApiEnvelope<SubscriptionCatchUpResult>> = await apiClient.post(
    `${BASE}/engine/subscriptions/refresh`, { window_days: windowDays },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Subscription refresh failed');
}

export async function clearSubscriptionError(subscriptionId: number): Promise<{ cleared: number }> {
  const res = await apiClient.post(`${BASE}/engine/subscriptions/${subscriptionId}/clear-error`);
  return res.data;
}

export async function listAnchorState(ruleId?: number): Promise<AnchorState[]> {
  const res: AxiosResponse<ApiEnvelope<AnchorState[]>> = await apiClient.get(
    `${BASE}/engine/anchor-state`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listAnchorStateLog(ruleId?: number, limit: number = 100): Promise<AnchorStateLog[]> {
  const res: AxiosResponse<ApiEnvelope<AnchorStateLog[]>> = await apiClient.get(
    `${BASE}/engine/anchor-state-log`,
    { params: { ...(ruleId ? { rule_id: ruleId } : {}), limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1084 — Date-Exception Application  BRDs 1469–1479
// ===========================================================================

export type ShiftOutcomeAction = 'strict' | 'shift_forward' | 'shift_backward' | 'shift_to_nearest';
export type ShiftOutcomeKind = 'on-day' | 'no_shift_strict' | 'shifted';

export interface ShiftOutcome {
  id: number;
  entity_id: number;
  run_id?: number;
  rule_id: number;
  scope_element_id?: number | null;
  original_date: string;
  final_date: string;
  action: ShiftOutcomeAction;
  outcome: ShiftOutcomeKind;
  reason?: string | null;
  distance_days: number;
  business_days?: number[] | null;
  holiday_calendar_id?: number | null;
  calendar_available: boolean;
  notes?: string | null;
  is_catch_up: boolean;
  created_on?: string;
}

export async function listRunShiftOutcomes(runId: number, ruleId?: number): Promise<ShiftOutcome[]> {
  const res: AxiosResponse<ApiEnvelope<ShiftOutcome[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/shift-outcomes`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleShiftOutcomes(ruleId: number, limit: number = 50): Promise<ShiftOutcome[]> {
  const res: AxiosResponse<ApiEnvelope<ShiftOutcome[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/shift-outcomes`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1085 — Event-Key Construction  BRDs 1480–1489
// ===========================================================================

export type EventKeySource = 'cadence' | 'human' | 'external_api';

export interface EventKeyConstruction {
  id: number;
  entity_id: number;             // BRD 2 — tenant_id component of the 4-tuple
  run_id?: number | null;
  rule_id: number;
  scope_element_id?: number | null;   // BRD 4 — geography_id (NULL ⇒ 'NONE')
  event_type_id: string;              // BRD 3 — stable source-derived id
  period_id: string;                  // BRD 5 — period_token from N#1082
  event_key: string;                  // BRD 6 — canonical 4-tuple natural key
  occurrence_as_of?: string | null;
  source: EventKeySource;             // BRD 9 — source-independent construction
  construction_notes?: string | null;
  is_catch_up: boolean;
  created_on?: string;
}

export async function listRunEventKeyConstructions(runId: number, ruleId?: number): Promise<EventKeyConstruction[]> {
  const res: AxiosResponse<ApiEnvelope<EventKeyConstruction[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/event-key-constructions`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleEventKeyConstructions(ruleId: number, limit: number = 50): Promise<EventKeyConstruction[]> {
  const res: AxiosResponse<ApiEnvelope<EventKeyConstruction[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/event-key-constructions`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1086 — Register Check (Skip-on-Exists)  BRDs 1490–1497
// ===========================================================================

export type RegisterCheckResult = 'exists' | 'not_exists' | 'race_detected';
export type InstanceSource = 'cadence' | 'form' | 'external_api';

export interface RegisterCheck {
  id: number;
  entity_id: number;
  run_id?: number | null;
  rule_id: number;
  scope_element_id?: number | null;
  event_key: string;
  result: RegisterCheckResult;
  existing_instance_id?: number | null;
  existing_instance_source?: InstanceSource | null;
  existing_created_at?: string | null;
  existing_source_rule_id?: number | null;     // BRD 5 — when source='cadence'
  existing_source_user_id?: number | null;     // BRD 5 — when source='form'
  is_dry_run: boolean;
  is_catch_up: boolean;
  notes?: string | null;
  created_on?: string;
}

export async function listRunRegisterChecks(runId: number, ruleId?: number): Promise<RegisterCheck[]> {
  const res: AxiosResponse<ApiEnvelope<RegisterCheck[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/register-checks`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleRegisterChecks(ruleId: number, limit: number = 50): Promise<RegisterCheck[]> {
  const res: AxiosResponse<ApiEnvelope<RegisterCheck[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/register-checks`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1087 — Handler Invocation (Fire Contract)  BRDs 1498–1505
// ===========================================================================

export type FireQueueStatus =
  | 'pending' | 'in_flight' | 'succeeded' | 'failed' | 'retrying' | 'dead_letter';

export interface FirePayload {
  tenant_id: number;
  rule_id: number;
  version: number;
  occurrence_number: number;
  scheduled_for: string;
  event_key: string;
  source: 'cadence';
  template_ref?: string | null;
  anchor_context?: any;
}

export interface HandlerFire {
  id: number;
  entity_id: number;
  run_id?: number | null;
  rule_id: number;
  scope_element_id?: number | null;
  event_key: string;
  scheduled_for: string;
  occurrence_number: number;
  rule_version: number;
  template_ref?: string | null;
  anchor_context?: any;
  payload: FirePayload;
  status: FireQueueStatus;
  attempts: number;
  retry_count: number;            // N#1088 BRD 5
  last_error?: string | null;
  error_code?: string | null;
  instance_id?: number | null;
  response_notes?: string | null;
  queued_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  latency_ms?: number | null;
  created_on?: string;
  modified_on?: string;
  // N#1089 retry/DLQ context
  next_retry_at?: string | null;
  retry_history?: any[];
  dlq_status?: 'active' | 're-fired' | 'waived' | 'archived' | null;
  dlq_alerted_on?: string | null;
  dlq_action_by?: number | null;
  dlq_action_on?: string | null;
  dlq_action_notes?: string | null;
}

export async function listRunHandlerFires(runId: number, ruleId?: number, status?: FireQueueStatus): Promise<HandlerFire[]> {
  const res: AxiosResponse<ApiEnvelope<HandlerFire[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/handler-fires`,
    { params: { ...(ruleId ? { rule_id: ruleId } : {}), ...(status ? { status } : {}) } },
  );
  return res.data.data || [];
}

export async function listRuleHandlerFires(ruleId: number, limit: number = 50): Promise<HandlerFire[]> {
  const res: AxiosResponse<ApiEnvelope<HandlerFire[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/handler-fires`,
    { params: { limit } },
  );
  return res.data.data || [];
}

export async function listPendingHandlerFires(limit: number = 100): Promise<HandlerFire[]> {
  const res: AxiosResponse<ApiEnvelope<HandlerFire[]>> = await apiClient.get(
    `${BASE}/engine/queue/pending`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1088 — Idempotency Keys  BRDs 1506–1512
// ===========================================================================

export type HandlerStatus = 'pending' | 'accepted' | 'revoked' | 'failed';
export type ContractTestStatus = 'running' | 'passed' | 'failed';

export interface HandlerRegistration {
  id: number;
  entity_id: number;
  category_id?: number | null;
  type_id?: number | null;
  handler_code: string;
  handler_endpoint?: string | null;
  handler_version: string;
  status: HandlerStatus;
  last_contract_test_id?: number | null;
  accepted_on?: string | null;
  revoked_on?: string | null;
  revoked_reason?: string | null;
  notes?: string | null;
  is_active: number;
  created_by?: number;
  created_on?: string;
  modified_on?: string;
  contract_tests?: HandlerContractTest[];
}

export interface HandlerContractTest {
  id: number;
  entity_id: number;
  handler_id: number;
  started_on?: string | null;
  finished_on?: string | null;
  duration_ms?: number | null;
  status: ContractTestStatus;
  retry_idempotent?: boolean | null;
  retry_first_instance_id?: number | null;
  retry_second_instance_id?: number | null;
  cross_source_idempotent?: boolean | null;
  cross_source_first_instance_id?: number | null;
  cross_source_second_instance_id?: number | null;
  notes?: string | null;
  failure_reason?: string | null;
  test_event_key?: string | null;
  test_rule_id?: number | null;
  test_occurrence_number?: number | null;
  created_by?: number;
  created_on?: string;
}

export interface HandlerRegistrationCreate {
  category_id?: number | null;
  type_id?: number | null;
  handler_code: string;
  handler_endpoint?: string | null;
  handler_version?: string;
  notes?: string | null;
}

export async function listHandlers(status?: HandlerStatus): Promise<HandlerRegistration[]> {
  const res: AxiosResponse<ApiEnvelope<HandlerRegistration[]>> = await apiClient.get(
    `${BASE}/engine/handlers`,
    { params: status ? { status } : {} },
  );
  return res.data.data || [];
}

export async function getHandler(handlerId: number): Promise<HandlerRegistration> {
  const res: AxiosResponse<ApiEnvelope<HandlerRegistration>> = await apiClient.get(
    `${BASE}/engine/handlers/${handlerId}`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Handler not found');
}

export async function registerHandler(payload: HandlerRegistrationCreate): Promise<HandlerRegistration> {
  const res: AxiosResponse<ApiEnvelope<HandlerRegistration>> = await apiClient.post(
    `${BASE}/engine/handlers`, payload,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to register handler');
}

export async function runHandlerContractTest(handlerId: number): Promise<HandlerContractTest> {
  const res: AxiosResponse<ApiEnvelope<HandlerContractTest>> = await apiClient.post(
    `${BASE}/engine/handlers/${handlerId}/contract-test`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Contract test failed');
}

export async function revokeHandler(handlerId: number, reason: string): Promise<HandlerRegistration> {
  const res: AxiosResponse<ApiEnvelope<HandlerRegistration>> = await apiClient.post(
    `${BASE}/engine/handlers/${handlerId}/revoke`, { reason },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Revoke failed');
}

export async function listHandlerContractTests(handlerId: number, limit: number = 20): Promise<HandlerContractTest[]> {
  const res: AxiosResponse<ApiEnvelope<HandlerContractTest[]>> = await apiClient.get(
    `${BASE}/engine/handlers/${handlerId}/contract-tests`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1089 — Retry & Dead-Letter Handling  BRDs 1513–1520
// ===========================================================================

export type DlqStatus = 'active' | 're-fired' | 'waived' | 'archived';
export type DlqAction = 'refire' | 'waive' | 'archive';

export interface RetryHistoryAttempt {
  attempt: number;
  attempted_at: string;
  finished_at: string;
  latency_ms: number;
  error_code: string;
  error: string;
}

export interface DeadLetterEntry extends HandlerFire {
  // dlq_status etc. already in HandlerFire after this migration
  actions?: DlqActionLogEntry[];
}

export interface DlqActionLogEntry {
  id: number;
  entity_id: number;
  queue_id: number;
  action: DlqAction;
  actor_user_id?: number | null;
  notes?: string | null;
  outcome?: string | null;
  new_queue_id?: number | null;
  new_instance_id?: number | null;
  created_on?: string;
}

export interface DlqActionRequest {
  notes?: string;
}

export async function listDlq(dlqStatus: DlqStatus | 'all' = 'active', limit: number = 100): Promise<DeadLetterEntry[]> {
  const res: AxiosResponse<ApiEnvelope<DeadLetterEntry[]>> = await apiClient.get(
    `${BASE}/engine/dlq`,
    { params: { dlq_status: dlqStatus, limit } },
  );
  return res.data.data || [];
}

export async function getDlqEntry(queueId: number): Promise<DeadLetterEntry> {
  const res: AxiosResponse<ApiEnvelope<DeadLetterEntry>> = await apiClient.get(
    `${BASE}/engine/dlq/${queueId}`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'DLQ entry not found');
}

export async function dlqRefire(queueId: number, notes?: string): Promise<{
  original_queue_id: number;
  new_queue_id: number;
  outcome: string;
  instance_id?: number | null;
  error_code?: string | null;
}> {
  const res = await apiClient.post(`${BASE}/engine/dlq/${queueId}/refire`, { notes });
  if (res.data?.success && res.data.data) return res.data.data;
  throw new Error(res.data?.detail || 'Refire failed');
}

export async function dlqWaive(queueId: number, notes: string): Promise<{ queue_id: number; dlq_status: DlqStatus }> {
  const res = await apiClient.post(`${BASE}/engine/dlq/${queueId}/waive`, { notes });
  if (res.data?.success && res.data.data) return res.data.data;
  throw new Error(res.data?.detail || 'Waive failed');
}

export async function dlqArchive(queueId: number, notes?: string): Promise<{ queue_id: number; dlq_status: DlqStatus }> {
  const res = await apiClient.post(`${BASE}/engine/dlq/${queueId}/archive`, { notes });
  if (res.data?.success && res.data.data) return res.data.data;
  throw new Error(res.data?.detail || 'Archive failed');
}

export async function listDlqActions(queueId: number): Promise<DlqActionLogEntry[]> {
  const res: AxiosResponse<ApiEnvelope<DlqActionLogEntry[]>> = await apiClient.get(
    `${BASE}/engine/dlq/${queueId}/actions`,
  );
  return res.data.data || [];
}

export async function processRetryQueue(limit: number = 50): Promise<{ processed: any[]; total: number }> {
  const res = await apiClient.post(`${BASE}/engine/retry-queue/process`, null, { params: { limit } });
  return res.data;
}

// ===========================================================================
// N#1090 — Version Resolution at Fire Time  BRDs 1521–1528
// ===========================================================================

export type VersionResolutionOutcome = 'resolved' | 'no_active_version' | 'error';
export type VersionResolutionSource = 'live' | 'snapshot' | 'none';

export interface VersionResolution {
  id: number;
  entity_id: number;
  run_id?: number | null;
  rule_id: number;
  target_date: string;
  is_catch_up: boolean;
  resolved_version_id?: number | null;
  resolved_version_number?: number | null;
  resolved_effective_from?: string | null;
  resolved_effective_until?: string | null;
  resolved_source?: VersionResolutionSource | null;
  outcome: VersionResolutionOutcome;
  error_message?: string | null;
  notes?: string | null;
  created_on?: string;
}

export interface VersionResolutionPreview {
  outcome: VersionResolutionOutcome;
  source: VersionResolutionSource;
  version_id?: number | null;
  version_number?: number | null;
  effective_from?: string | null;
  effective_until?: string | null;
  error_message?: string | null;
  notes?: string | null;
  target_date: string;
  is_catch_up: boolean;
}

export async function listRunVersionResolutions(runId: number, ruleId?: number): Promise<VersionResolution[]> {
  const res: AxiosResponse<ApiEnvelope<VersionResolution[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/version-resolutions`,
    { params: ruleId ? { rule_id: ruleId } : {} },
  );
  return res.data.data || [];
}

export async function listRuleVersionResolutions(ruleId: number, limit: number = 50): Promise<VersionResolution[]> {
  const res: AxiosResponse<ApiEnvelope<VersionResolution[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/version-resolutions`,
    { params: { limit } },
  );
  return res.data.data || [];
}

export async function previewVersionResolution(
  ruleId: number, targetDate: string, isCatchUp: boolean = false,
): Promise<VersionResolutionPreview> {
  const res: AxiosResponse<ApiEnvelope<VersionResolutionPreview>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/resolve-version`,
    { params: { target_date: targetDate, is_catch_up: isCatchUp } },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Version resolution failed');
}

// ===========================================================================
// N#1091 — Fire Logging  BRDs 1529–1537
// ===========================================================================

export type FireLogStatus =
  | 'fired' | 'succeeded' | 'skipped' | 'error' | 'retrying'
  | 'waived' | 'dead_lettered'
  | 'would_create' | 'would_skip' | 'would_shift';

export type FireLogSource = 'cadence' | 'form' | 'external_api';

export interface FireLogEntry {
  id: number;
  entity_id: number;
  cadence_rule_id: number;
  run_id?: number | null;
  scheduled_date: string;
  fired_at?: string | null;
  fire_status: FireLogStatus;
  source: FireLogSource;
  event_key?: string | null;
  instance_id?: number | null;
  instance_ref?: string | null;
  geography_id?: number | null;
  period_token?: string | null;
  occurrence_number?: number | null;
  retry_count: number;
  resolved_version_number?: number | null;
  resolved_version_id?: number | null;
  resolved_effective_from?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  http_response_body?: string | null;
  stack_trace?: string | null;
  retry_history?: any[];
  skip_reason?: string | null;
  source_rule_id?: number | null;
  transitions?: FireLogTransition[];
}

export interface FireLogTransition {
  id: number;
  entity_id: number;
  fire_log_id: number;
  from_status?: FireLogStatus | null;
  to_status: FireLogStatus;
  transition_reason?: string | null;
  retry_count_at_transition: number;
  error_code?: string | null;
  error_message?: string | null;
  changed_by?: number | null;
  changed_at?: string | null;
}

export interface FireLogSourceRecon {
  source: FireLogSource;
  fired_count: number;
  skipped_count: number;
  error_count: number;
  dead_lettered_count: number;
  waived_count: number;
  total: number;
}

export interface FireLogFilters {
  rule_id?: number;
  fire_status?: FireLogStatus;
  source?: FireLogSource;
  version?: number;
  geography_id?: number;
  period_token?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export async function listRunFireLog(runId: number, ruleId?: number, fireStatus?: FireLogStatus): Promise<FireLogEntry[]> {
  const res: AxiosResponse<ApiEnvelope<FireLogEntry[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/fire-log`,
    { params: { ...(ruleId ? { rule_id: ruleId } : {}), ...(fireStatus ? { fire_status: fireStatus } : {}) } },
  );
  return res.data.data || [];
}

export async function listFireLog(filters: FireLogFilters = {}): Promise<FireLogEntry[]> {
  const res: AxiosResponse<ApiEnvelope<FireLogEntry[]>> = await apiClient.get(
    `${BASE}/engine/fire-log`,
    { params: filters },
  );
  return res.data.data || [];
}

export async function getFireLogEntry(fireLogId: number): Promise<FireLogEntry> {
  const res: AxiosResponse<ApiEnvelope<FireLogEntry>> = await apiClient.get(
    `${BASE}/fire-log/${fireLogId}`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Fire log entry not found');
}

export async function listFireLogTransitions(fireLogId: number): Promise<FireLogTransition[]> {
  const res: AxiosResponse<ApiEnvelope<FireLogTransition[]>> = await apiClient.get(
    `${BASE}/fire-log/${fireLogId}/transitions`,
  );
  return res.data.data || [];
}

export async function getFireLogSourceReconciliation(opts: { date_from?: string; date_to?: string; rule_id?: number } = {}): Promise<FireLogSourceRecon[]> {
  const res: AxiosResponse<ApiEnvelope<FireLogSourceRecon[]>> = await apiClient.get(
    `${BASE}/engine/fire-log/source-reconciliation`,
    { params: opts },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1092 — Versioning Model  BRDs 1538–1544  (Bead B#147)
// ===========================================================================

export type VersionState = 'draft' | 'queued' | 'active' | 'historical';

export interface VersionLifecycle {
  rule_id: number;
  current_version_number: number;
  draft?: RuleVersion | null;
  queued?: RuleVersion | null;
  active?: RuleVersion | null;
  historical_count: number;
}

export async function getVersionLifecycle(ruleId: number): Promise<VersionLifecycle> {
  const res: AxiosResponse<ApiEnvelope<VersionLifecycle>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/versions/lifecycle`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to load lifecycle');
}

export async function createVersionDraft(ruleId: number, notes?: string): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/versions/draft`,
    { notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to create draft');
}

export async function getVersionDraft(ruleId: number): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/versions/draft`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'No draft for this rule');
}

export async function updateVersionDraft(ruleId: number, full_snapshot?: any, notes?: string): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.patch(
    `${BASE}/rules/${ruleId}/versions/draft`,
    { full_snapshot, notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to update draft');
}

export type RolloutMode = 'immediate' | 'future_effective';

export async function rolloutVersionDraft(
  ruleId: number,
  opts: { mode?: RolloutMode; effective_from?: string; notes?: string } = {},
): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/versions/draft/rollout`,
    { mode: opts.mode || 'immediate',
      effective_from: opts.effective_from,
      notes: opts.notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Rollout failed');
}

export async function editQueuedVersion(ruleId: number, reason?: string): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/versions/queued/edit`,
    { reason },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Failed to reopen queued version');
}

export async function promoteQueuedVersions(): Promise<{ rule_id: number; version_number: number; active_from: string }[]> {
  const res = await apiClient.post(`${BASE}/engine/versions/promote-queued`);
  return res.data.data || [];
}

// ===========================================================================
// N#1093 — Roll-out & Effective Date  BRDs 1545–1551
// ===========================================================================

export type RolloutAuditAction =
  | 'rollout_immediate' | 'rollout_future_effective'
  | 'effective_date_changed' | 'auto_promoted' | 'queued_to_draft';

export interface RolloutAudit {
  id: number;
  entity_id: number;
  rule_id: number;
  version_id: number;
  version_number: number;
  action: RolloutAuditAction;
  mode?: RolloutMode | null;
  previous_state?: VersionState | null;
  new_state?: VersionState | null;
  previous_effective_from?: string | null;
  new_effective_from?: string | null;
  previous_active_version_id?: number | null;
  previous_active_version_number?: number | null;
  actor_user_id?: number | null;
  notes?: string | null;
  created_on?: string;
}

export async function changeQueuedEffectiveDate(
  ruleId: number, new_effective_from: string, notes?: string,
): Promise<RuleVersion> {
  const res: AxiosResponse<ApiEnvelope<RuleVersion>> = await apiClient.post(
    `${BASE}/rules/${ruleId}/versions/queued/effective-date`,
    { new_effective_from, notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Change effective-date failed');
}

export async function listRolloutAudit(ruleId: number, limit: number = 50): Promise<RolloutAudit[]> {
  const res: AxiosResponse<ApiEnvelope<RolloutAudit[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/versions/rollout-audit`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// N#1094 — Mid-Period Transition Handling  BRDs 1552–1557
// ===========================================================================

export type MidPeriodStatus = 'pending' | 'backfilled' | 'skipped';

export interface MidPeriodTransition {
  id: number;
  entity_id: number;
  rule_id: number;
  old_version_id?: number | null;
  old_version_number?: number | null;
  new_version_id: number;
  new_version_number: number;
  period_token?: string | null;
  period_scheduled_date?: string | null;
  added_scope_element_ids: number[];
  removed_scope_element_ids: number[];
  status: MidPeriodStatus;
  resolved_by?: number | null;
  resolved_on?: string | null;
  resolution_notes?: string | null;
  backfill_queue_ids: number[];
  backfill_instance_ids: number[];
  backfill_outcomes: { scope_element_id: number; outcome: string; instance_id?: number | null }[];
  detected_on?: string | null;
  detection_source?: string | null;
  created_on?: string;
  modified_on?: string;
}

export async function listMidPeriodTransitions(opts: { status?: MidPeriodStatus; rule_id?: number; limit?: number } = {}): Promise<MidPeriodTransition[]> {
  const res: AxiosResponse<ApiEnvelope<MidPeriodTransition[]>> = await apiClient.get(
    `${BASE}/engine/mid-period-transitions`,
    { params: opts },
  );
  return res.data.data || [];
}

export async function getMidPeriodTransition(tid: number): Promise<MidPeriodTransition> {
  const res: AxiosResponse<ApiEnvelope<MidPeriodTransition>> = await apiClient.get(
    `${BASE}/engine/mid-period-transitions/${tid}`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Mid-period transition not found');
}

export async function skipMidPeriodTransition(tid: number, notes?: string): Promise<MidPeriodTransition> {
  const res: AxiosResponse<ApiEnvelope<MidPeriodTransition>> = await apiClient.post(
    `${BASE}/engine/mid-period-transitions/${tid}/skip`,
    { notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Skip failed');
}

export async function backfillMidPeriodTransition(tid: number, notes?: string): Promise<MidPeriodTransition> {
  const res: AxiosResponse<ApiEnvelope<MidPeriodTransition>> = await apiClient.post(
    `${BASE}/engine/mid-period-transitions/${tid}/backfill`,
    { notes },
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Backfill failed');
}

// ===========================================================================
// N#1095 — Catch-Up Across Version Boundary  BRDs 1558–1563
// ===========================================================================

export type CatchUpOutcome = 'fired' | 'partial' | 'skipped' | 'error' | 'no_active_version';

export interface CatchUpAttempt {
  id: number;
  entity_id: number;
  run_id?: number | null;
  rule_id: number;
  target_date: string;
  reason?: string | null;
  version_resolution_id?: number | null;
  resolved_version_id?: number | null;
  resolved_version_number?: number | null;
  resolved_effective_from?: string | null;
  resolved_source?: 'live' | 'snapshot' | 'none' | null;
  outcome: CatchUpOutcome;
  scope_elements_attempted: number;
  scope_elements_fired: number;
  scope_elements_skipped: number;
  scope_elements_errored: number;
  fire_outcomes: any[];
  fire_log_ids: number[];
  instance_ids: number[];
  error_message?: string | null;
  notes?: string | null;
  actor_user_id?: number | null;
  fired_at?: string | null;
  created_on?: string;
}

export async function runCatchUpFire(
  ruleId: number,
  payload: { target_date: string; reason?: string; notes?: string },
): Promise<{ outcome: CatchUpOutcome; attempt: CatchUpAttempt; run_id?: number;
              fired?: number; skipped?: number; errored?: number;
              scope_elements_attempted?: number }> {
  const res = await apiClient.post(`${BASE}/rules/${ruleId}/catch-up`, {
    target_date: payload.target_date,
    reason: payload.reason || 'admin',
    notes: payload.notes,
  });
  if (res.data?.success) return { ...res.data.data, run_id: res.data.run_id };
  throw new Error(res.data?.detail || 'Catch-up failed');
}

export async function listCatchUpAttempts(opts: { rule_id?: number; outcome?: CatchUpOutcome; limit?: number } = {}): Promise<CatchUpAttempt[]> {
  const res: AxiosResponse<ApiEnvelope<CatchUpAttempt[]>> = await apiClient.get(
    `${BASE}/engine/catch-up-attempts`,
    { params: opts },
  );
  return res.data.data || [];
}

export async function listCatchUpAttemptsForRun(runId: number): Promise<CatchUpAttempt[]> {
  const res: AxiosResponse<ApiEnvelope<CatchUpAttempt[]>> = await apiClient.get(
    `${BASE}/engine/runs/${runId}/catch-up-attempts`,
  );
  return res.data.data || [];
}

export async function listCatchUpAttemptsForRule(ruleId: number, limit: number = 50): Promise<CatchUpAttempt[]> {
  const res: AxiosResponse<ApiEnvelope<CatchUpAttempt[]>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/catch-up-attempts`,
    { params: { limit } },
  );
  return res.data.data || [];
}

// ===========================================================================
// Dry-run Preview — who will receive this obligation if the rule fires?
// (Per-rule operator-facing preview rendered in DryRunModal.)
// ===========================================================================

export interface DryRunScopeRow {
  location_id?: number | null;
  location_name?: string | null;
  location_type?: string | null;
  scope_status: 'active' | 'missing';
  accountable_role_id?: number | null;
  accountable_role_name?: string | null;
  accountable_user_id?: number | null;
  accountable_user_name?: string | null;
  accountable_user_email?: string | null;
  fallback_role_id?: number | null;
  fallback_role_name?: string | null;
  fallback_user_id?: number | null;
  fallback_user_name?: string | null;
  fallback_user_email?: string | null;
  final_user_id?: number | null;
  final_user_name?: string | null;
  final_user_email?: string | null;
  assignment_note: 'direct' | 'fallback used' | 'Unassigned';
  will_fire: boolean;
  skip_reason?: string | null;
  applicability_passed?: boolean | null;
  applicability_reason?: string | null;
  scheduled_date?: string | null;
  period_token?: string | null;
}

export interface DryRunPreview {
  rule_id: number;
  rule_name?: string | null;
  rule_status?: string | null;
  as_of: string;
  accountable_role_name?: string | null;
  fallback_role_name?: string | null;
  total_scope_elements: number;
  will_fire_count: number;
  will_skip_count: number;
  unassigned_count: number;
  fallback_used_count: number;
  rows: DryRunScopeRow[];
  notes: string[];
}

export async function previewRuleDryRun(ruleId: number): Promise<DryRunPreview> {
  const res: AxiosResponse<ApiEnvelope<DryRunPreview>> = await apiClient.get(
    `${BASE}/rules/${ruleId}/dry-run-preview`,
  );
  if (res.data.success && res.data.data) return res.data.data;
  throw new Error(res.data.error || 'Dry-run preview failed');
}

// ── Tier 1 (Tentative cadence) ─────────────────────────────────────────────
export interface TentativeFire {
  id: number;
  rule_id: number;
  rule_version: number;
  occurrence_number: number;
  scheduled_for: string;
  status: string;
  period_token: string | null;
  scope_element_id: number | null;
  geography_name: string | null;
  authority_id: number | null;
  authority_name: string | null;
}

export async function runEnqueueTentative(ruleId?: number, includeDrafts = false): Promise<{ rules_processed: number; tentative_inserted: number }> {
  const res = await apiClient.post(`${BASE}/engine/enqueue`, null, {
    params: { ...(ruleId ? { rule_id: ruleId } : {}), include_drafts: includeDrafts },
  });
  return res.data?.data;
}

export async function listFires(tier: 'tentative' | 'formed', ruleId?: number): Promise<TentativeFire[]> {
  const res: AxiosResponse<ApiEnvelope<TentativeFire[]>> = await apiClient.get(`${BASE}/engine/fires`, {
    params: { tier, ...(ruleId ? { rule_id: ruleId } : {}) },
  });
  return res.data.data || [];
}

/** Server-paginated fire list (Tier 1 queue / Tier 2 log) — real COUNT-backed
 *  total + LIMIT/OFFSET on the server. */
export async function listFiresPaged(
  tier: 'tentative' | 'formed',
  opts: { page: number; limit: number; ruleId?: number },
): Promise<PagedResult<TentativeFire>> {
  const res = await apiClient.get(`${BASE}/engine/fires`, {
    params: { tier, page: opts.page, limit: opts.limit, ...(opts.ruleId ? { rule_id: opts.ruleId } : {}) },
  });
  const d: any = res.data ?? {};
  return { items: Array.isArray(d.data) ? d.data : [], total: d.meta?.total ?? d.total ?? 0 };
}

export async function purgeFires(tier: 'tentative' | 'formed' | 'all', ruleId?: number): Promise<{ queue_deleted: number; fire_log_deleted: number }> {
  const res = await apiClient.delete(`${BASE}/engine/fires`, {
    params: { tier, ...(ruleId ? { rule_id: ruleId } : {}) },
  });
  return res.data?.data;
}

// ── Tier 2 (Formed-up cadence) ─────────────────────────────────────────────
export async function runFormup(start: string, windowDays: number, ruleId?: number): Promise<{
  window: { start: string; end: string; days: number };
  occurrences_in_window: number;
  calendar_committed: number;
  actions_dispatched: Record<string, number>;
}> {
  const res = await apiClient.post(`${BASE}/engine/formup`, null, {
    params: { start, window_days: windowDays, ...(ruleId ? { rule_id: ruleId } : {}) },
  });
  return res.data?.data;
}

// ---------------------------------------------------------------------------
// Insights — cross-module cadence dashboard
// ---------------------------------------------------------------------------

export interface CadenceCount {
  label: string;
  count: number;
}

export interface CadenceInsights {
  rules_total: number;
  rules_active: number;
  runs_total: number;
  runs_scheduled: number;
  last_run_at: string | null;
  last_run: {
    started_at: string | null;
    status: string | null;
    trigger_source: string | null;
    instances_created: number;
    instances_skipped: number;
    rules_evaluated: number;
    rules_failed: number;
  } | null;
  firmed_upcoming: number;
  tentative_upcoming: number;
  upcoming_instances: number;
  rules_by_status: CadenceCount[];
  rules_by_module: CadenceCount[];
  instances_by_status: CadenceCount[];
}

export async function getCadenceInsights(): Promise<CadenceInsights> {
  const res = await apiClient.get(`${BASE}/insights`);
  return res.data?.data;
}
